import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Box, Button, Popover, Stack, Tooltip, Typography } from "@mui/material";
import WbSunnyRounded from "@mui/icons-material/WbSunnyRounded";
import DarkModeRounded from "@mui/icons-material/DarkModeRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import {
  getMarketState,
  getFreshnessState,
  getMockLastUpdate,
  formatAgo
} from "@/lib/market-status";
import type { MarketState, FreshnessState } from "@/lib/market-status";

/**
 * Market-status UI is split into two visually distinct pieces:
 *
 *  • `MarketHoursIcon`  — a full Moon or Sun icon. Placed at the right
 *    end of the hero Total line so it reads as "the market is open /
 *    closed" without competing with the number for attention.
 *  • `FreshnessDot`     — a small coloured dot rendered *as an index*
 *    (superscript-style) immediately after the Total number. It tells
 *    the user "this figure is live" (green) or "this figure is cached"
 *    (yellow) and is intentionally subtle because most of the time
 *    nothing has changed.
 *
 * Both pieces share a single `useMarketStatus` hook so they agree on
 * the current time and the mock `lastUpdate` timestamp and don't drift
 * from each other. The hook also owns a 30-second tick so the "X min
 * ago" text inside the tooltip stays current without remounts.
 *
 * When a live quote feed ships, callers can pass `lastUpdate={feed.ts}`
 * to override the mock clock — the default falls back to a deterministic
 * demo value that still makes the dot flip states believably.
 */

// ---- Shared hook ----------------------------------------------------------

interface MarketStatusSnapshot {
  marketState: MarketState;
  freshness: FreshnessState;
  lastUpdateAt: Date;
  now: Date;
}

/**
 * Derives the current market + freshness snapshot and ticks every 30s
 * so age-sensitive UI stays current. `lastUpdate` is optional — when
 * omitted the hook uses `getMockLastUpdate` (demo clock).
 */
function useMarketStatus(lastUpdate?: Date): MarketStatusSnapshot {
  // We don't need the tick value — just a re-render signal. `setTick`
  // increments a counter so `useMemo` below re-derives.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  return useMemo(() => {
    const n = new Date();
    const updatedAt = lastUpdate ?? getMockLastUpdate(n);
    return {
      marketState: getMarketState(n),
      freshness: getFreshnessState(updatedAt, n),
      lastUpdateAt: updatedAt,
      now: n
    };
  // Intentionally keying on `tick` to force re-derivation on the
  // interval; `lastUpdate` may also change if the caller passes a live
  // feed timestamp.
  }, [lastUpdate, tick]);
}

// ---- MarketHoursIcon ------------------------------------------------------

interface MarketHoursIconProps {
  /** Optional last-update override — forwarded for tooltip consistency. */
  lastUpdate?: Date;
  /** Icon size in px. Defaults to 20 — reads clearly without crowding. */
  size?: number;
  /**
   * When true, render a small "Market open" / "Market closed" caption
   * next to the icon. Collapses cleanly on narrow containers via flex
   * wrap / overflow, so the caller can rely on it to disappear when
   * there's no room. Added Apr-2026 per user feedback — the icon
   * alone required tooltip interaction to understand; the inline
   * label makes the state legible at a glance when there's space.
   */
  showLabel?: boolean;
}

/**
 * Full Moon/Sun icon indicating whether US equities are currently in a
 * regular trading session. Tooltip surfaces the detail (session window,
 * time-ago). Hover or tap to read. Optionally renders a caption next to
 * the icon for at-a-glance legibility when the container has room.
 */
export function MarketHoursIcon({
  lastUpdate, size = 20, showLabel = false
}: MarketHoursIconProps) {
  const { marketState } = useMarketStatus(lastUpdate);
  const open = marketState === "open";

  // Colour rationale:
  //  • Sun (open) — amber/gold so it reads as "sun" at a glance, not
  //    just "yellow". Matches the typical daylight affordance.
  //  • Moon (closed) — a cool slate/indigo; explicitly *not* red so
  //    "market closed" doesn't read as an error state.
  const color = open ? "#f59e0b" : "#64748b";
  const tooltip = open
    ? "US equities market open — regular session (9:30–16:00 ET, Mon–Fri)"
    : "US equities market closed";
  const label = open ? "Market open" : "Market closed";

  const Icon = open ? WbSunnyRounded : DarkModeRounded;
  return (
    <Tooltip
      title={<Typography variant="caption">{tooltip}</Typography>}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      placement="top"
    >
      <Box
        role="img"
        aria-label={label}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: showLabel ? 0.5 : 0,
          cursor: "help",
          color,
          lineHeight: 0,
          // Small focus ring for keyboard users.
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 3,
            borderRadius: "50%"
          }
        }}
        tabIndex={0}
      >
        {/* Label first, icon second — per Apr-2026 round-11 feedback:
         *  "Market Close/Open and its icon should switch places". Reads
         *  left-to-right as "Market closed 🌙" / "Market open ☀", which
         *  matches the way the rest of the hero reads (value → qualifier). */}
        {showLabel && (
          <Typography
            variant="caption"
            sx={{
              lineHeight: 1,
              fontWeight: 600,
              color: "text.secondary",
              whiteSpace: "nowrap"
            }}
          >
            {label}
          </Typography>
        )}
        <Icon sx={{ fontSize: size }} />
      </Box>
    </Tooltip>
  );
}

// ---- FreshnessDot (index on Total) ----------------------------------------

interface FreshnessDotProps {
  /** Optional last-update override; defaults to the mock clock. */
  lastUpdate?: Date;
  /** Dot size in px — superscript-style, so intentionally small. */
  size?: number;
}

/**
 * Small coloured dot rendered as an *index* next to a headline number
 * — sits flush to the top-right of the digit, like a footnote marker.
 *
 * Green = quotes live (<5 min during RTH) or today's EOD close after
 * hours. Yellow = cached / older than the freshness window. Tap or
 * hover to read the "updated X ago" detail.
 */
export function FreshnessDot({ lastUpdate, size = 7 }: FreshnessDotProps) {
  const { freshness, marketState, lastUpdateAt, now } = useMarketStatus(lastUpdate);
  const fresh = freshness === "fresh";

  const tooltip = fresh
    ? marketState === "open"
      ? `Quotes live · updated ${formatAgo(lastUpdateAt, now)}`
      : `Today's closing quotes · updated ${formatAgo(lastUpdateAt, now)}`
    : `Cached data · last updated ${formatAgo(lastUpdateAt, now)}`;

  const color = fresh ? "#22c55e" : "#eab308";

  return (
    <Tooltip
      title={<Typography variant="caption">{tooltip}</Typography>}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      placement="top"
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={`${fresh ? "Data fresh" : "Data cached"} — ${tooltip}`}
        sx={{
          // Superscript baseline alignment. `vertical-align: super`
          // only works on inline text; we're using a Box so we emulate
          // it with a negative top margin that lifts the dot toward
          // the cap-height of the digit it sits next to.
          display: "inline-block",
          width: size,
          height: size,
          borderRadius: "50%",
          bgcolor: color,
          verticalAlign: "top",
          ml: 0.25,
          mt: 0.5,
          cursor: "pointer",
          position: "relative",
          // 24px tap target via an invisible enlargement ring — meets
          // WCAG 2.5.5 without affecting layout.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: -8,
            borderRadius: "50%"
          },
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 3
          }
        }}
      />
    </Tooltip>
  );
}

// ---- CachedDataPopover (click-to-open on hero number) --------------------

interface CachedDataPopoverProps {
  /** The element users click to open the popover — typically the big
   *  hero $ Typography. Must be a single ReactNode; we wrap it in a
   *  span so click tracking works on any content. */
  children: ReactNode;
  /**
   * Optional last-update override — defaults to the mock clock. This is
   * a FULLY CONTROLLED prop: the popover does NOT stamp its own
   * "justUpdated" timestamp on button click. The caller owns the
   * lastUpdate state (so pressing Update here can also flip the
   * sibling `FreshnessDot` to green), and the popover just fires
   * `onUpdate` for the caller to bump its state.
   */
  lastUpdate?: Date;
  /**
   * Caller-supplied refresh hook. Fires when the user clicks the
   * "Update" button inside the popover. The caller is expected to set
   * a fresh `lastUpdate` (typically `new Date()`) in response so both
   * this popover and the FreshnessDot recompute as "just updated".
   */
  onUpdate?: () => void;
}

/**
 * Click-to-open popover anchored on the hero Portfolio Total number.
 * Surfaces the same "Quotes live / Cached data · last updated X ago"
 * copy the `FreshnessDot` shows in its tooltip, plus an "Update" text
 * button for the user to request a fresh quote.
 *
 * Built as a proper MUI Popover (rather than a Tooltip) because the
 * body hosts an interactive button — Tooltip's auto-dismiss on pointer
 * leave fights with button clicks. Styling mirrors `MetricTip` so the
 * hero popover visually matches the Metric Card tooltips elsewhere
 * on the page (Apr-2026 R12 feedback: "show cached data tooltip like
 * on the example, the same as cached data icon").
 */
export function CachedDataPopover({
  children, lastUpdate, onUpdate
}: CachedDataPopoverProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Fully-controlled: no internal `justUpdated` shadow. The caller owns
  // the timestamp so pressing Update here can simultaneously flip the
  // sibling FreshnessDot to green via shared state (Apr-2026 R12-5b).
  const { freshness, marketState, lastUpdateAt, now } =
    useMarketStatus(lastUpdate);
  const fresh = freshness === "fresh";

  // Data-status copy (Apr-2026 R12-6a feedback: "show the Data status,
  // e.g. Status: Latest Data (No need to update)"). Three derived
  // combinations — expressed as a single "Status:" line so the user
  // gets a consistent verdict regardless of open/closed:
  //  • Fresh + market open → "Latest Data" with a "Live quote, X ago" detail
  //  • Fresh + market closed → "Latest Data" with "No need to update —
  //    reflects most recent close". Applies uniformly to ANY closed
  //    period (weekend, weekday pre-market, after-hours) — the
  //    freshness rule in `getFreshnessState` accepts any update at/after
  //    the most-recent elapsed 16:00 ET close, so e.g. a Tuesday 03:00
  //    session landing on Monday's 16:00 print reads the same as a
  //    Sunday landing on Friday's print.
  //  • Stale → "Cached Data" with "Last updated X ago"
  const headline = fresh ? "Status: Latest Data" : "Status: Cached Data";
  const detail = fresh
    ? marketState === "open"
      ? `Live quote · updated ${formatAgo(lastUpdateAt, now)}`
      : "No need to update — reflects most recent close"
    : `Last updated ${formatAgo(lastUpdateAt, now)}`;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleUpdate = () => {
    onUpdate?.();
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/*
        Block-level wrapper (NOT a span) so the caller's headline
        Typography keeps its natural element (e.g. <h1>) without
        illegal block-in-inline nesting — putting an <h1> inside a
        <span> caused browsers to demote the hero number to a smaller
        render size (Apr-2026 R12 feedback: "return back font size of
        Total Portfolio value").
        `display: inline-flex` keeps the wrapper sitting inline on the
        hero row (so MarketHoursIcon still pins to the right edge via
        `justify-content: space-between`) while remaining a valid
        block-level parent for any heading-level child.
      */}
      <Box
        component="div"
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            (e.currentTarget as HTMLElement).click();
          }
        }}
        sx={{
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "baseline",
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 4,
            borderRadius: 1
          }
        }}
      >
        {children}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              // Styling mirrors MetricTip's tooltip panel so the two
              // look like siblings — same surface colour, border, pad,
              // rounded corners, max width. Only change: we render a
              // real Popover (anchored MUI surface) instead of a
              // Tooltip because the body hosts an interactive button.
              bgcolor: "#1a1d25",
              color: "#e2e4e9",
              border: "1px solid #2d3040",
              borderRadius: 2,
              p: 1.5,
              mt: 0.75,
              maxWidth: 320,
              boxShadow: "0 12px 32px rgba(0,0,0,0.45)"
            }
          }
        }}
      >
        {/*
          Typography scale (Apr-2026 R12-6b/c feedback): the Update
          button text renders at `variant="body1"` (1rem) to match the
          "Buying Power" header size elsewhere on the hero, and the
          Refresh icon is pinned to 24px to match the range-row icon
          scale (calendar / rebase / gear). The Status headline uses the
          same body1 size + bold weight so the popover reads as a
          labelled data-status card rather than a mini caption box.
          Do NOT shrink these back to caption size — the previous pass
          (0.8125rem caption + 16px icon) looked like mismatched chrome.
        */}
        <Stack spacing={0.75}>
          <Typography
            variant="body1"
            sx={{
              display: "block",
              fontWeight: 700,
              color: "#e2e4e9",
              lineHeight: 1.25
            }}
          >
            {headline}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#9ca3af", lineHeight: 1.4 }}
          >
            {detail}
          </Typography>
          <Button
            variant="text"
            onClick={handleUpdate}
            startIcon={<RefreshRounded sx={{ fontSize: "24px !important" }} />}
            sx={{
              alignSelf: "flex-start",
              mt: 0.25,
              px: 0.5,
              minHeight: 32,
              fontSize: "1rem",
              lineHeight: 1.25,
              fontWeight: 600,
              textTransform: "none",
              // Blue text-button so the primary action reads as tappable
              // without competing with the copy above for visual weight.
              color: "#60a5fa",
              "&:hover": { bgcolor: "rgba(96,165,250,0.08)" }
            }}
          >
            Update
          </Button>
        </Stack>
      </Popover>
    </>
  );
}

// ---- Deprecated combined indicator (kept as a thin forwarder) -------------

/**
 * @deprecated Use `<MarketHoursIcon />` and `<FreshnessDot />`
 * independently so you can place them in different parts of the hero
 * (icon at the right edge of the Total line; dot as an index on the
 * number itself). This forwarder stays so existing callers — if any —
 * compile without changes; remove once the V2 refactor is done.
 */
export function MarketStatusIndicator(props: MarketHoursIconProps) {
  return (
    <>
      <MarketHoursIcon {...props} />
      <FreshnessDot lastUpdate={props.lastUpdate} />
    </>
  );
}
